import { routes } from 'constants/routes';

export const visierIframeId = 'visier-app';

let visierGlobals = {
  enableDebugLogging: true,
  isAppLoaded: false,
  keepSessionAliveTimer: undefined, // Set while bootstrapping Visier.
  appIframe: document.getElementById(visierIframeId),
  errorCallback: null,
  eventsCallback: null,
};

const INTERVAL_DURATION = 200000;

export const updateVisierGlobals = (values) => {
  visierGlobals = { ...visierGlobals, ...values };
};

export function navigateVisier(url) {
  window.visier('trigger', 'NAVIGATE', url);
}

function showAppIframe() {
  visierGlobals.appIframe.style.display = 'block';
}

function renderVisierErrorMessage(message, data) {
  if (visierGlobals.errorCallback) {
    visierGlobals.errorCallback(message, data);
  }
  showAppIframe();

  if (message) {
    console.error(message, data || 'No additional information supplied.');
  }
}

/**
 * Prevent the Visier session from expiring to provide a smooth user experience. Visier sessions
 * last 55 minutes. It is recommended you renew the Visier session every 10-15 minutes.
 */

function keepVisierSessionAlive() {
  window.visier('trigger', 'PARENT_SESSION_ALIVE');
}

/**
 * Add event handlers for session events
 */
function attachSessionEventHandlers(embeddingApp) {
  embeddingApp.on('session', (msg) => {
    if (visierGlobals.eventsCallback) {
      visierGlobals.eventsCallback(msg);
    }
    switch (msg?.code?.toUpperCase()) {
      case 'EJECT_SESSION':
        /**
         * This message is emitted if the user opens another tab that embeds content for a different user or tenant.
         * The session on this tab will be cleaned up. Any continued attempts to use this session will result in
         * unexpected behavior such as showing data for the other user or tenant.
         *
         * A partner application must handle this event gracefully and prevent further embedding interactions
         * such as by removing the iframes or disabling navigation actions.
         */

        renderVisierErrorMessage('The user logged into another tenant on another tab or window.');
        break;
      case 'SESSION_ERROR':
        /**
         * Scenarios in which this message may be emitted include, but are not limited to:
         *   1. The SSO configuration for the specified Visier tenant is incorrect or disabled.
         *   2. Auto-provisioning is disabled and the user does not have a Visier profile.
         *   3. The `tenantCode` claim in the SAML assertion was invalid.
         *   4. The SAML assertion sent to Visier's ACS URL was malformed
         *
         * A partner application must handle this event gracefully, as it represents a blocked user flow.
         */
        renderVisierErrorMessage('The user could not be logged into Visier.', msg.message);
        break;
      case 'SESSION_ESTABLISHED':
        /**
         * This message is emitted once an authenticated session has been established with Visier for the current user.
         */
        // 10 minute interval. See `keepVisierSessionAlive()` for more details.
        visierGlobals.keepSessionAliveTimer = setInterval(() => keepVisierSessionAlive(), INTERVAL_DURATION);
        showAppIframe();
        break;
      case 'USER_AUTO_PROVISION_FAILED':
        /**
         * A partner application must handle this event gracefully, as it represents a blocked user flow.
         */
        renderVisierErrorMessage('Auto-provisioning failed.', msg.message);
        break;
      case 'USER_AUTO_PROVISION_SUCCESS':
        /**
         * This message is emitted when a new Visier user is created automatically for the current user.
         * Auto-provisioning must be enabled in your Visier tenant's settings under "Tenant Single Sign-On"
         * and it must be the first time this user is using Visier.
         */
        break;
      case 'VISIER_SESSION_ALIVE':
        /**
         * These events are emitted when the user is active in the Visier application. This allows the parent application to
         * keep its own user session alive when the user is active in Visier but not in the parent application. By default,
         * these messages are emitted once every five minutes, but this can be configured in your Visier tenant under "Embedded App Config".
         */
        // For this example application, user sessions do not expire, so no action is needed.
        break;
      case 'VISIER_SESSION_EXPIRED':
        /**
         * If the user is active in the parent application but not in Visier, the user's session in Visier can expire.
         * Note: Sending the Visier application iframe `PARENT_SESSION_ALIVE` messages, as in `keepVisierSessionAlive()`,
         * prevents the Visier session from expiring. Best practice is to handle expired Visier sessions nonetheless.
         */
        // Call `embeddingApp.bootstrapSession();` to create a new session in the background.
        // Once complete, new requests will pick up the new authentication tokens.
        // Alternatively, call embedApp to restart the process again, including authentication.
        // This will reload the application, which will help clear any errors surface earlier due to the session expiry.
        embeddingApp.embedApp(visierIframeId);
        break;
      case 'AUTO_LOGIN_FAILED':
        renderVisierErrorMessage('Auto login failed', msg.message);
        showAppIframe();
        break;
      default:
        break;
    }
  });
}

/**
 * Add event handlers for error events
 */
function attachErrorEventHandlers(embeddingApp) {
  embeddingApp.on('error', (msg) => {
    switch (msg?.code?.toUpperCase()) {
      case 'CONTAINER_MISSING':
        /**
         * If the container to embed the content in cannot be found.
         */
        renderVisierErrorMessage('Invalid container', msg);
        break;
      case 'FAILED_TO_EJECT_PREVIOUS_SESSION':
        /**
         * If the user opens a second tab that embeds content for a different user or tenant, this error is emitted
         * in the case of unexpected errors while performing cleanup on the first tab.
         *
         * The partner application can clean up the other session and attempt embedding once again.
         */
        renderVisierErrorMessage(
          "Multiple users are not supported. Clean up previous user sessions before establishing a new user's session."
        );
        break;
      case 'INVALID_APP_URL':
        /**
         * Either an invalid URL was passed to the embedApp method or no default URL could be found.
         */
        renderVisierErrorMessage('Invalid Visier app url found', msg);
        break;
      case 'SW_LOADER_TIMEOUT':
      case 'SW_LOADING_TIMEOUT':
        /**
         * These timeout errors emit if there are unexpected issues with the authentication set up or if the set up took
         * longer than expected, such as due to network latency. Possible causes include:
         *  - The IdP failed to authenticate the user. Check the Network tab.
         *  - The service worker failed to load. Look for errors in the Console tab and turn on enableDebugLogging.
         */
        renderVisierErrorMessage(
          'Unexpected error loading the embedded app. Try extending the requestTimeout in case of network latency.'
        );
        break;
      case 'VISIER_APP_DOWN':
        /**
         * When Visier is unavailable, render and log an error message.
         */
        renderVisierErrorMessage('Visier currently unavailable', msg);
        break;
      default:
        break;
    }
  });
}

/**
 * Add event handlers for debug events.
 * These are short messages to help better facilitate joint debugging between teams.
 * We've set this up so that it only logs if you set enableDebugLogging to true.
 */
function attachDebugEventHandlers(embeddingApp) {
  if (visierGlobals.enableDebugLogging) {
    embeddingApp.on('debug', (msg) => {
      console.debug('Received debug message from Visier:', msg);
    });
  }
}

/**
 * Add event handlers for info events
 */
function attachInfoEventHandlers(embeddingApp) {
  embeddingApp.on('info', (msg) => {
    if (visierGlobals.eventsCallback) {
      visierGlobals.eventsCallback(msg);
    }
    switch (msg?.code?.toUpperCase()) {
      case 'VISIER_APP_LOADED':
        /**
         * This event is intended to prevent partners from loading the Visier solution multiple times and to support session
         * clean up. See `cleanUpVisierSession()` for more details.
         */

        visierGlobals.isAppLoaded = true;
        break;

      default:
        break;
    }
  });
}

/**
 * ************************************************************************************************
 * 6. Call embedApp
 *    This will render the application in the given iframe. Check for query parameters and pass in
 *    an optional URL to your call to specify where to start.
 * ************************************************************************************************
 */

async function embed(embeddingApp, landingUrl) {
  if (landingUrl) {
    embeddingApp.embedApp(visierIframeId, landingUrl);
  } else {
    embeddingApp.embedApp(visierIframeId);
  }
}

export function visierSDKScript(handleVisierEvents, handleVisierErrors, landingURL) {
  visierGlobals.appIframe = document.getElementById(visierIframeId);

  // Set up the `visierConfig`. The `visierConfig` bootstraps the SDK.
  const visierConfig = {
    // Set the URL to use for logging into Visier.
    visierUrl: 'https://mesh.visier.com/hr',
    requestTimeout: 50000,

    // Optional: Set the URL for your IDP.
    // The IdP URL that handles hidden sessions and posts valid SAML assertions.
    // idpUrl: endpoints.embeddedAnalytics.connect,
    idpUrl: `${window.location.origin}${routes.embeddedAnalytics.connectIframe}`,
  };

  function embedVisier(sdkPath) {
    let scriptElement = null;
    const scriptTagName = 'script';
    const visierText = 'visier';
    window.VisierEmbedder = visierText;
    window[visierText] =
      window[visierText] ||
      function visierFunction(...args) {
        (window[visierText].q = window[visierText].q || []).push(args);
      };
    scriptElement = document.createElement(scriptTagName);
    const [firstScriptElement] = document.getElementsByTagName(scriptTagName);
    scriptElement.async = true;
    scriptElement.src = visierConfig.visierUrl + sdkPath;
    firstScriptElement.parentNode.insertBefore(scriptElement, firstScriptElement);
  }

  embedVisier('/assets/embedded/webAssets/sdk.v2.js');

  // Bootstrap must always be the first call to `visier()`.
  window.visier('bootstrap', visierConfig, async (app) => {
    window.embeddingApp = app;
    visierGlobals.errorCallback = handleVisierErrors;
    visierGlobals.eventsCallback = handleVisierEvents;
    attachSessionEventHandlers(app);
    attachErrorEventHandlers(app);
    attachInfoEventHandlers(app);
    attachDebugEventHandlers(app);
    embed(app, landingURL);
  });
}
